import { useCallback, useEffect, useMemo, useState } from "react";
import { usePrevious } from "react-use";
import { useSnackbar } from "notistack";
import type { Step } from "ui";

import { useGetLayersQuery, useGetVersionQuery, useUpdateVersionArchitectureMutation } from "fond/api";
import { DEFAULT_DATA_PREP_CONFIGURATION } from "fond/architecture/dataPrepConfiguration";
import { useCustomWorkflow, WorkflowState } from "fond/hooks/useCustomWorkflow";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import { loadProject } from "fond/project/redux";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";

import { BaseDataGating, getBaseDataGating, isWorkflowRunning } from "./gating";
import {
  BaseDataMissingLayers,
  BaseDataPresence,
  GENERATION_KEYS,
  GENERATION_WORKFLOW_KEYS,
  GenerationKey,
  getBaseDataAnyPresence,
  getBaseDataMissingLayers,
  getBaseDataPresence,
} from "./layers";
import { getGenerationProgressSteps, getLatestFailure } from "./progress";
import { getCompletedEndTime, getStaleness, Staleness } from "./staleness";

type UseBaseDataGenerationProps = {
  projectId: string | undefined;
  versionId: string;
  readOnly: boolean;
};

export type BaseDataGeneration = {
  present: BaseDataPresence;
  /** Whether each base data layer has data in any of its planner layers. */
  hasData: BaseDataPresence;
  missing: BaseDataMissingLayers;
  staleness: Staleness;
  gating: BaseDataGating;
  workflowStates: Record<GenerationKey, WorkflowState>;
  /** Generation workflows currently starting or in progress. */
  running: GenerationKey[];
  /** Generation workflows waiting for a running one to finish before they start. */
  queued: GenerationKey[];
  /** Whether any generation workflow is running or queued. */
  isRunning: boolean;
  /** Whether a run is being prepared, before any workflow has started. */
  isStarting: boolean;
  /** The most recently run generation workflow, if its execution failed. */
  failed: GenerationKey[];
  /** Progress of the running generation workflows, or of the failed ones when nothing is running. */
  progressSteps: Step[];
  /** Run the given generation workflows in order, first asking for confirmation if they would replace existing layers and the caller hasn't already confirmed. */
  run(keys: readonly GenerationKey[], options?: { confirmed?: boolean }): void;
  cancel(): void;
  /** Generation workflows awaiting the user's confirmation to replace their existing layers. */
  confirmation: GenerationKey[] | null;
  confirmRun(): void;
  dismissConfirmation(): void;
};

/** Return the state of base data generation for a version, and controls to run and cancel it. */
export const useBaseDataGeneration = ({ projectId, versionId, readOnly }: UseBaseDataGenerationProps): BaseDataGeneration => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const { data: layers } = useGetLayersQuery(versionId, { skip: !versionId });
  const { layerKeys } = useGetPlannerLayers(versionId);
  const [updateVersionArchitecture, { isLoading: isSavingArchitecture }] = useUpdateVersionArchitectureMutation();
  const [queue, setQueue] = useState<GenerationKey[]>([]);
  const [chain, setChain] = useState<GenerationKey[]>([]);
  const [confirmation, setConfirmation] = useState<GenerationKey[] | null>(null);

  const handleOnComplete = useCallback(() => {
    if (projectId) dispatch(loadProject({ uuid: projectId, forceRefetch: true }));
  }, [dispatch, projectId]);

  const ug = useCustomWorkflow({ versionId, key: GENERATION_WORKFLOW_KEYS.ug, onComplete: handleOnComplete });
  const leadins = useCustomWorkflow({ versionId, key: GENERATION_WORKFLOW_KEYS.leadins, onComplete: handleOnComplete });
  const legacy = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.PLANNER_EXTENDED.DATA_PREP });
  const runWorkflows = useMemo(() => ({ ug: ug.runWorkflow, leadins: leadins.runWorkflow }), [ug.runWorkflow, leadins.runWorkflow]);
  const cancelWorkflows = useMemo(() => ({ ug: ug.cancelWorkflow, leadins: leadins.cancelWorkflow }), [ug.cancelWorkflow, leadins.cancelWorkflow]);

  const present = useMemo(() => getBaseDataPresence(layerKeys, Boolean(version?.UploadedArea)), [layerKeys, version?.UploadedArea]);
  const missing = useMemo(() => getBaseDataMissingLayers(layerKeys), [layerKeys]);
  const hasData = useMemo(() => getBaseDataAnyPresence(missing, Boolean(version?.UploadedArea)), [missing, version?.UploadedArea]);

  const ugEndTime = getCompletedEndTime(ug.workflowState, ug.endTime);
  const leadinsEndTime = getCompletedEndTime(leadins.workflowState, leadins.endTime);
  const legacyEndTime = getCompletedEndTime(legacy.workflowState, legacy.endTime);
  const layerLastModified = useMemo(
    () => Object.fromEntries(Object.values(layers?.entities ?? {}).map((layer) => [layer.LayerKey, layer.UpdateTime])),
    [layers]
  );
  const staleness = useMemo(
    () =>
      getStaleness({
        layerLastModified,
        ugGenerationEndTime: ugEndTime,
        leadinGenerationEndTime: leadinsEndTime,
        legacyDataPrepEndTime: legacyEndTime,
        architectureLastModifiedAt: version?.Architecture?.LastModifiedAt,
      }),
    [layerLastModified, ugEndTime, leadinsEndTime, legacyEndTime, version?.Architecture?.LastModifiedAt]
  );

  const workflowStates: Record<GenerationKey, WorkflowState> = useMemo(
    () => ({ ug: ug.workflowState, leadins: leadins.workflowState }),
    [ug.workflowState, leadins.workflowState]
  );
  const running = useMemo(() => GENERATION_KEYS.filter((key) => isWorkflowRunning(workflowStates[key])), [workflowStates]);
  const latestFailure = getLatestFailure({
    ug: { state: ug.workflowState, endTime: ug.endTime },
    leadins: { state: leadins.workflowState, endTime: leadins.endTime },
  });
  const failed = useMemo(() => (latestFailure ? [latestFailure] : []), [latestFailure]);
  const isRunning = running.length > 0 || queue.length > 0;

  const gating = useMemo(
    () =>
      getBaseDataGating({
        present,
        staleness,
        running,
        hasArchitecture: Boolean(version?.Architecture),
        readOnly,
      }),
    [present, staleness, running, version?.Architecture, readOnly]
  );

  const progressSteps = useMemo(
    () =>
      getGenerationProgressSteps({
        keys: isRunning ? GENERATION_KEYS.filter((key) => chain.includes(key) || running.includes(key)) : failed,
        queued: queue,
        stages: { ug: ug.stages, leadins: leadins.stages },
      }),
    [isRunning, chain, running, failed, queue, ug.stages, leadins.stages]
  );

  const start = useCallback(
    async (keys: readonly GenerationKey[]) => {
      const [first, ...rest] = keys;
      const runWorkflow = first && runWorkflows[first];
      if (!runWorkflow) return;

      // Architectures saved outside planner_extended may lack a data prep configuration, so ensure we set one.
      if (version?.Architecture && !version.Architecture.DataPrepConfiguration) {
        try {
          await updateVersionArchitecture({ versionId, architecture: { DataPrepConfiguration: DEFAULT_DATA_PREP_CONFIGURATION } }).unwrap();
        } catch {
          enqueueSnackbar("Failed to save Data Prep Settings.", { variant: "error" });
          return;
        }
      }

      setChain([...keys]);
      setQueue(rest);
      await runWorkflow();
    },
    [runWorkflows, version?.Architecture, updateVersionArchitecture, versionId, enqueueSnackbar]
  );

  // Queued workflows wait on UG generation: start the next when it completes, and abandon them if it fails or is cancelled.
  const previousUgState = usePrevious(ug.workflowState);
  useEffect(() => {
    if (queue.length === 0 || previousUgState === ug.workflowState || isWorkflowRunning(ug.workflowState)) return;
    if (ug.workflowState === "COMPLETE") {
      const [next, ...rest] = queue;
      setQueue(rest);
      runWorkflows[next]?.();
    } else {
      setQueue([]);
    }
  }, [queue, previousUgState, ug.workflowState, runWorkflows]);

  const run = useCallback(
    (keys: readonly GenerationKey[], { confirmed = false }: { confirmed?: boolean } = {}) => {
      if (!confirmed && keys.some((key) => hasData[key])) {
        setConfirmation([...keys]);
      } else {
        start(keys);
      }
    },
    [hasData, start]
  );

  const confirmRun = useCallback(() => {
    if (confirmation) start(confirmation);
    setConfirmation(null);
  }, [confirmation, start]);

  const dismissConfirmation = useCallback(() => setConfirmation(null), []);

  const cancel = useCallback(() => {
    setQueue([]);
    running.forEach((key) => cancelWorkflows[key]?.());
  }, [running, cancelWorkflows]);

  return useMemo(
    () => ({
      present,
      hasData,
      missing,
      staleness,
      gating,
      workflowStates,
      running,
      queued: queue,
      isRunning,
      isStarting: isSavingArchitecture,
      failed,
      progressSteps,
      run,
      cancel,
      confirmation,
      confirmRun,
      dismissConfirmation,
    }),
    [
      present,
      hasData,
      missing,
      staleness,
      gating,
      workflowStates,
      running,
      queue,
      isRunning,
      isSavingArchitecture,
      failed,
      progressSteps,
      run,
      cancel,
      confirmation,
      confirmRun,
      dismissConfirmation,
    ]
  );
};
